/**
 * StructuredLoggingStrategy
 *
 * Concrete implementation of ILoggingStrategy
 * Centralized structured logging with correlation IDs
 *
 * Single Responsibility: Structured logging
 */

import type { ILoggingStrategy, LogEntry } from '../interfaces';
import type { LogLevel } from '../../logging';
import type { LogContext, HookExecutionInfo, SubscriberDeliveryInfo } from '../types';

export class StructuredLoggingStrategy implements ILoggingStrategy {
  private logs: LogEntry[] = [];
  private logLevel: LogLevel = 'info';
  private enabled = true;
  private maxLogSize = 1000;
  private stats = {
    debug: 0,
    info: 0,
    warn: 0,
    error: 0,
  };

  createContext(correlationId: string): LogContext {
    return {
      correlationId,
      timestamp: new Date(),
      level: 'info',
    };
  }

  debug(message: string, context: LogContext, data?: Record<string, unknown>): void {
    this.log('debug', message, context, data);
  }

  info(message: string, context: LogContext, data?: Record<string, unknown>): void {
    this.log('info', message, context, data);
  }

  warn(message: string, context: LogContext, data?: Record<string, unknown>): void {
    this.log('warn', message, context, data);
  }

  error(message: string, error: Error, context: LogContext, data?: Record<string, unknown>): void {
    this.log('error', message, context, {
      ...data,
      error: {
        name: error.name,
        message: error.message,
        stack: error.stack,
      },
    });
  }

  logConnection(
    event: 'connected' | 'disconnected' | 'reconnecting' | 'failed',
    context: LogContext,
    data?: Record<string, unknown>
  ): void {
    const messages: Record<string, string> = {
      connected: 'Connected to SignalR hub',
      disconnected: 'Disconnected from hub',
      reconnecting: 'Attempting reconnection',
      failed: 'Connection failed',
    };

    this.log(
      event === 'failed' ? 'error' : event === 'connected' ? 'info' : 'warn',
      messages[event],
      context,
      { event, ...data }
    );
  }

  logHandlerRegistered(eventName: string, handlerName: string, context: LogContext): void {
    this.log('debug', `Handler registered for event: ${eventName}`, context, {
      eventName,
      handlerName,
    });
  }

  logHookExecution(messageId: string, hookInfo: HookExecutionInfo): void {
    const level = hookInfo.status === 'error' ? 'error' : 'debug';
    const message = `Hook execution: ${hookInfo.hookName} (${hookInfo.status})`;

    this.log(level, message, { correlationId: messageId, timestamp: hookInfo.timestamp, level }, {
      hookName: hookInfo.hookName,
      priority: hookInfo.priority,
      status: hookInfo.status,
      duration: hookInfo.duration,
      description: hookInfo.description,
      impactLevel: hookInfo.impactLevel,
    });
  }

  logTransformation(messageId: string, context: LogContext, data: any): void {
    const level = data.status === 'error' ? 'warn' : 'debug';
    this.log(level, `Message transformation: ${data.status}`, context, {
      messageId,
      ...data,
    });
  }

  logSubscriberDelivery(messageId: string, deliveryInfo: SubscriberDeliveryInfo): void {
    const level = deliveryInfo.status === 'error' ? 'warn' : 'debug';
    const message = `Subscriber delivery: ${deliveryInfo.subscriberId} (${deliveryInfo.status})`;

    this.log(level, message, { correlationId: messageId, timestamp: deliveryInfo.timestamp, level }, {
      subscriberId: deliveryInfo.subscriberId,
      topic: deliveryInfo.topic,
      status: deliveryInfo.status,
      processingTime: deliveryInfo.processingTime,
      impactLevel: deliveryInfo.impactLevel,
    });
  }

  logMessageLifecycle(
    messageId: string,
    stage: 'received' | 'pipeline-start' | 'pipeline-complete' | 'delivered' | 'failed',
    context: LogContext,
    data?: Record<string, unknown>
  ): void {
    const message = `Message lifecycle: ${stage}`;
    this.log(stage === 'failed' ? 'error' : 'info', message, context, {
      messageId,
      stage,
      ...data,
    });
  }

  logHealthCheck(
    serviceName: string,
    healthy: boolean,
    context: LogContext,
    details?: Record<string, unknown>
  ): void {
    const level = healthy ? 'info' : 'warn';
    const status = healthy ? 'healthy' : 'unhealthy';
    this.log(level, `Health check: ${serviceName} ${status}`, context, {
      serviceName,
      healthy,
      ...details,
    });
  }

  getLogs(correlationId?: string): LogEntry[] {
    if (!correlationId) return [...this.logs];
    return this.logs.filter((log) => log.correlationId === correlationId);
  }

  clearLogs(): void {
    this.logs = [];
  }

  getStatistics() {
    return {
      totalLogs: this.logs.length,
      byLevel: {
        debug: this.stats.debug,
        info: this.stats.info,
        warn: this.stats.warn,
        error: this.stats.error,
      },
      byCategory: this.getLogsByCategory(),
      oldestLog: this.logs[0]?.timestamp,
      newestLog: this.logs[this.logs.length - 1]?.timestamp,
    };
  }

  setLogLevel(level: LogLevel): void {
    this.logLevel = level;
  }

  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
  }

  // Private methods

  private log(
    level: LogLevel,
    message: string,
    context: LogContext,
    data?: Record<string, unknown>
  ): void {
    if (!this.enabled || !this.shouldLog(level)) return;

    const entry: LogEntry = {
      correlationId: context.correlationId,
      timestamp: new Date(),
      level,
      category: this.getCategory(message),
      message,
      context: data,
    };

    this.logs.push(entry);
    this.stats[level]++;

    // Trim logs if exceeds max size
    if (this.logs.length > this.maxLogSize) {
      this.logs = this.logs.slice(-this.maxLogSize);
    }

    // Console output
    this.consoleOutput(entry);
  }

  private shouldLog(level: LogLevel): boolean {
    const levels = ['debug', 'info', 'warn', 'error'];
    return levels.indexOf(level) >= levels.indexOf(this.logLevel);
  }

  private getCategory(message: string): string {
    if (message.includes('Connection')) return 'Connection';
    if (message.includes('Handler')) return 'Handler';
    if (message.includes('Hook')) return 'Hook';
    if (message.includes('Pipeline')) return 'Pipeline';
    if (message.includes('Subscriber')) return 'Subscriber';
    if (message.includes('Health')) return 'Health';
    return 'General';
  }

  private getLogsByCategory(): Record<string, number> {
    const categories: Record<string, number> = {};
    for (const log of this.logs) {
      categories[log.category] = (categories[log.category] ?? 0) + 1;
    }
    return categories;
  }

  private consoleOutput(entry: LogEntry): void {
    const prefix = `[${entry.timestamp.toISOString()}] [${entry.correlationId}] [${entry.level.toUpperCase()}]`;
    const output = `${prefix} ${entry.message}`;

    switch (entry.level) {
      case 'debug':
        console.debug(output, entry.context);
        break;
      case 'info':
        console.log(output, entry.context);
        break;
      case 'warn':
        console.warn(output, entry.context);
        break;
      case 'error':
        console.error(output, entry.context);
        break;
    }
  }
}
